import React, { useState, useCallback, useMemo, useContext } from "react";
import ReevooBadge from "./reevoo-badge";
import useEnergyClassImg from "../hooks/use-energy-class-img";
import { CDN_BASE_URL } from "../utility/constants";
import useEnergyLabelLink from "../utility/energy-label-link";
import RepairabilityIndex from "./repairability-index";
import Check from "../icons/check-blue.svg";
import CircleMinus from "../icons/circle-minus-light.svg";
import CirclePlus from "../icons/circle-plus-light.svg";
import PdpActions from "./pdp-actions";
import CompareContext from "../context/compare";
import CartContext from "../context/cart";
import useNoRtl from "../hooks/use-no-rtl";
import { formatPrice } from "../utility/prices";
import ScrollLink from "./scroll-link";
import CircleArrowDown from "../icons/circle-arrow-down.svg";
import CircleArrowUp from "../icons/circle-arrow-up.svg";
import classNames from "classnames";
import sortPosition from "../utility/sort-position";
import BannerFeaturesPdp from "./banner-features-pdp";
import { hasEcommerce } from "../utility/ecommerce";
import { eventInternalLinkClick } from "../utility/datalayer";

const ExtraService = ({
  name,
  descriptionHtml,
  sku,
  special_from_date,
  special_to_date,
  msrpPrice,
  sellingPrice,
  priceLanguage,
  flagDefault,
  labelManagerLabels,
  position,
  pagePath,
}) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = useCallback(() => setOpen((value) => !value), []);
  const { value, currency } = msrpPrice;
  const specialPriceValue = sellingPrice?.value !== value ? sellingPrice?.value : null;
  const price = specialPriceValue || specialPriceValue === 0 ? specialPriceValue : value;
  const specialPriceNull = !specialPriceValue && !special_from_date && !special_to_date;
  const includedPrice =
    !specialPriceNull &&
    (((value === null || value === 0) && (sellingPrice === null || sellingPrice === 0)) ||
      (sellingPrice === null && value > 0))
      ? 0
      : price;

  const onClickExtraService = useCallback(() => {
    eventInternalLinkClick({
      click_element: "pdp_service",
      position: `pdp_${position}`,
      link_text: sku ? `pdp_${sku}` : null,
      action: !open ? "pdp_open" : "pdp_close",
      slug: pagePath,
    });
  }, [position, open, pagePath, sku]);

  return (
    <div className={`box-accordion${open ? " open" : ""} box-accordion-${sku}`}>
      <button
        className="box-accordion__title w-100 d-flex flex-row align-items-center"
        onClick={() => {
          toggleOpen();
          onClickExtraService();
        }}
        disabled={!descriptionHtml}
      >
        <span className="d-flex align-items-center text-left mr-1">
          <img
            className="icon-svg"
            src={`${CDN_BASE_URL}/extra-services/${sku}.svg`}
            alt={name || ""}
          />
          {name}
        </span>
        <span className="mt-1 mt-md-0 d-flex align-items-center">
          {!specialPriceNull &&
            value > 0 &&
            (sellingPrice === null ||
              sellingPrice === 0 ||
              sellingPrice?.value === 0 ||
              sellingPrice?.value < value) && (
              <span className={`price line-through`}>
                {formatPrice(value, currency, priceLanguage)}
              </span>
            )}

          <span className="included">
            {includedPrice === 0 && flagDefault
              ? labelManagerLabels.includedService
              : formatPrice(includedPrice, currency, priceLanguage)}
          </span>
          <div className="box-accordion__button">
            <CircleArrowDown
              className={classNames("circle-arrow-down", !open ? "d-block ml-2" : "d-none")}
            />
            <CircleArrowUp
              className={classNames("circle-arrow-up", open ? "d-block ml-2" : "d-none")}
            />
          </div>
        </span>
      </button>
      {descriptionHtml && (
        <div className="box-accordion__content">
          <div
            className="box-accordion__content-inner"
            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
          />
        </div>
      )}
    </div>
  );
};

const DettaglioProdottoIntro = ({
  data,
  priceData,
  extraServices,
  config,
  labelManagerLabels,
  siteMetadata: { priceLanguage },
  location,
  bannerNotification,
  contributoRaee,
  storeLink,
  origin,
  linkProductPage,
  bannerFeatures,
  pagePath,
}) => {
  const {
    key_prod: productId,
    lineId,
    productCode,
    productTitle,
    energyClass,
    familiesIds,
    triples,
    minicard2,
    minicard4,
    productDescBefore,
    productDescAfter,
    pageLine,
    repairabilityIndex,
  } = data;

  const keySellingPoints = useMemo(() => {
    const productFamily = familiesIds && familiesIds.length ? familiesIds[0] : null;
    if (!triples && !productFamily) return null;
    const concatTriples = productFamily ? productFamily.triples.concat(triples) : triples;
    const allPoints = concatTriples.filter((item) => !!item.keySellingPoint);
    // Fallback su minicard, max 8
    return allPoints.length ? allPoints.map((item) => item.keySellingPoint) : minicard2;
  }, [familiesIds, triples, minicard2]);

  const { productIds, toggleProduct } = useContext(CompareContext);

  //TODO agganciare il productId giusto a reevoo
  const { addProduct, adding, loadingCart } = useContext(CartContext);
  const energyClassImg = useEnergyClassImg(energyClass, lineId, config);

  const LANG = useMemo(() => priceLanguage.split("-")[0].toUpperCase(), [priceLanguage]);

  const energyClassLink = useEnergyLabelLink(data, LANG, config);

  // Leggi di più
  const [more, setMore] = useState(false);
  const toggleMore = useCallback(() => setMore((value) => !value), []);

  const [description, setDescription] = useState(false);
  const toggleDescription = useCallback(() => setDescription((value) => !value), []);

  const pageLineNames = pageLine.pageName;
  const lineProductName2 =
    pageLineNames && pageLineNames[1] && pageLineNames[1] ? pageLineNames[1] : "";

  const readMoreDescription = productDescAfter || minicard4.length > (lineProductName2 ? 7 : 8);

  const noRtl = useNoRtl("pdp");

  const onToggleProduct = useCallback(() => toggleProduct(productId), [toggleProduct, productId]);

  const bannerFeaturesModules = useMemo(
    () =>
      bannerFeatures?.length
        ? bannerFeatures?.filter((module) => module?.titleFieldSet?.title)?.sort(sortPosition)
        : bannerFeatures,
    [bannerFeatures]
  );
  const enabledForLine = [undefined, "[]"].includes(
    data.pageLine?.lineConfigurationFieldSet?.isNotEcommerce?.[0]
  );
  const ecommerceActive = hasEcommerce && enabledForLine;

  const onClickReadMoreKsp = useCallback(() => {
    eventInternalLinkClick({
      click_element: "pdp_read_more_ksp",
      action: !more ? "pdp_open" : "pdp_close",
      slug: pagePath,
    });
  }, [more, pagePath]);

  const onClickCompare = useCallback(() => {
    eventInternalLinkClick({
      click_element: "pdp_compare",
      action: !productIds.includes(productId) ? "pdp_open" : "pdp_close",
      slug: pagePath,
    });
  }, [productId, productIds, pagePath]);

  return (
    <>
      <div className="product-detail__main-description">
        <div className="product-detail__names d-none d-md-flex justify-content-between align-items-center">
          <div className="d-flex w-100 product-detail__title-container">
            <h1 className="product-detail__title-wrapper">
              <span
                className="d-none d-md-block product-detail__title mt-2"
                dangerouslySetInnerHTML={{ __html: productTitle }}
              ></span>
              <span className="product-detail__code-energy-wrapper flex-wrap">
                <span className="product-detail__code mr-3">{productCode}</span>
                <span>
                  {energyClass &&
                    energyClass !== "-" &&
                    (energyClassLink ? (
                      <a href={energyClassLink} target="_blank" rel="noreferrer">
                        <img
                          src={energyClassImg}
                          className="icon-energy-rating"
                          alt={energyClass}
                        />
                      </a>
                    ) : (
                      <img src={energyClassImg} className="icon-energy-rating" alt={energyClass} />
                    ))}
                  {!!repairabilityIndex[0] && (
                    <RepairabilityIndex
                      data={
                        repairabilityIndex[0]?.product?.filter(
                          (index) => index.sku === productId
                        )[0]
                      }
                      lineId={data.lineId}
                    />
                  )}
                </span>
              </span>
            </h1>
          </div>
          {!!config.isReevooActiveFieldSet.isReevooActive?.length && (
            <div className="product-detail__reevoo">
              <ReevooBadge productId={productId} />
            </div>
          )}
        </div>
        <div
          className={classNames(
            `product-detail__main-features product-detail__${lineId} product-detail__${lineId}-${productId}`,
            keySellingPoints?.length < 4 && "product-detail__main-features--low"
          )}
        >
          <div className={`d-none d-md-block product-detail__desc${description ? " open" : ""}`}>
            <div>
              <p>
                {productDescBefore
                  ? productDescBefore
                  : minicard4.length
                  ? lineProductName2
                    ? lineProductName2 + " " + minicard4.slice(0, 7).join(", ")
                    : minicard4.slice(0, 8).join(", ")
                  : ""}{" "}
                {origin !== "AcModal" && !readMoreDescription && (
                  <ScrollLink href={`#technical-spec`} className="product-detail__view-st">
                    <span>{labelManagerLabels.seeAllTechnicalSpec}</span>
                  </ScrollLink>
                )}
                {readMoreDescription && !description ? "..." : ""}
              </p>
              {readMoreDescription && (
                <button
                  onClick={toggleDescription}
                  className={description ? "d-none" : "d-inline-block"}
                  aria-label="open Button"
                >
                  <CirclePlus />
                </button>
              )}

              {data?.compareProducts?.length > 1 && data?.lineId !== 333 && (
                <label className="product-detail__compare-label">
                  <input
                    type="checkbox"
                    checked={productIds.includes(productId)}
                    onChange={() => {
                      onToggleProduct();
                      onClickCompare();
                    }}
                    name={"pdp-compare"}
                  />
                  <span className="product-detail__compare-labelText">
                    {labelManagerLabels.compare}
                  </span>
                </label>
              )}
            </div>
            {readMoreDescription && (
              <>
                <div className="extended">
                  {productDescAfter
                    ? productDescAfter
                    : ", ".concat(minicard4.slice(lineProductName2 ? 7 : 8).join(", "))}
                  <button
                    onClick={toggleDescription}
                    aria-label="close Button"
                    className={description ? "d-inline-block less" : "d-none"}
                  >
                    <CircleMinus />
                  </button>
                </div>
              </>
            )}
            {origin !== "AcModal" && readMoreDescription && (
              <ScrollLink href={`#technical-spec`} className="product-detail__view-st ml-0">
                <span>{labelManagerLabels.seeAllTechnicalSpec}</span>
              </ScrollLink>
            )}
          </div>
          <div className="product-detail__features">
            <>
              <ul>
                {keySellingPoints.map(
                  (item, i) =>
                    i < 3 && (
                      <li key={i}>
                        <Check className="icon mr-2" />
                        <span>{item}</span>
                      </li>
                    )
                )}
              </ul>
              <ul
                className={`mt-0 product-detail__features-more-content ${
                  more ? "d-block" : "d-none"
                }`}
              >
                {keySellingPoints.slice(3, keySellingPoints.length).map((item, i) => (
                  <li key={i}>
                    <Check className="icon mr-2" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              {keySellingPoints.length >= 4 && (
                <button
                  className="product-detail__features-more"
                  onClick={() => {
                    toggleMore();
                    onClickReadMoreKsp();
                  }}
                >
                  <span className={more ? "d-none" : "d-inline"}>
                    {labelManagerLabels.readMore}
                  </span>
                  <span
                    className={
                      more ? "d-inline product-detail__features-more--icon-rotate" : "d-none"
                    }
                  >
                    {labelManagerLabels.readLess}
                  </span>
                </button>
              )}
            </>
          </div>
          {data?.compareProducts?.length > 1 && data?.lineId !== 333 && (
            <label className="product-detail__compare-label d-md-none">
              <input
                type="checkbox"
                checked={productIds.includes(productId)}
                onChange={onToggleProduct}
                name={"pdp-compare"}
              />
              <span className="product-detail__compare-labelText">
                {labelManagerLabels.compare}
              </span>
            </label>
          )}
        </div>
      </div>
      <PdpActions
        priceData={priceData}
        labelManagerLabels={labelManagerLabels}
        contributoRaee={contributoRaee}
        bannerNotification={bannerNotification}
        config={config}
        data={data}
        location={location}
        productIds={productIds}
        toggleProduct={toggleProduct}
        addProduct={addProduct}
        adding={adding}
        loadingCart={loadingCart}
        storeLink={storeLink}
        origin={origin}
        linkProductPage={linkProductPage}
        pagePath={pagePath}
      />

      {!!bannerFeaturesModules?.length && (
        <div className="product-detail__banner-features">
          {bannerFeaturesModules?.map((module, i) => (
            <BannerFeaturesPdp
              data={module?.titleFieldSet}
              key={i}
              position={i + 1}
              pagePath={pagePath}
            />
          ))}
        </div>
      )}

      {extraServices?.length && (ecommerceActive || (priceData && enabledForLine)) && !noRtl ? (
        <div className="product-detail__accordion">
          <p className="product-detail__accordion-title">{labelManagerLabels.additionalServices}</p>
          {extraServices.map((item, index) => (
            <ExtraService
              {...item}
              key={item.sku}
              priceLanguage={priceLanguage}
              flagDefault={item?.default}
              labelManagerLabels={labelManagerLabels}
              position={index + 1}
              pagePath={pagePath}
            />
          ))}
        </div>
      ) : //TODO loading
      null}
    </>
  );
};

export default DettaglioProdottoIntro;
